'use client';

import { useEffect } from 'react';
import { mergeClasses } from 'minimal-shared/utils';

import Link from '@mui/material/Link';
import Avatar from '@mui/material/Avatar';
import SvgIcon from '@mui/material/SvgIcon';
import { styled } from '@mui/material/styles';

import { newSearchParams } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { useSearchParams } from 'src/routes/hooks';

import { useTranslate } from 'src/locales';

import { logoClasses, useLogoStore } from './';

// ----------------------------------------------------------------------

export function Logo({ ref, dinamic, disabled, isSingle, className, href = '/', sx, ...other }) {
  const searchParams = useSearchParams();

  const { currentLang } = useTranslate();

  const { photoURL, resetPhotoURL } = useLogoStore();

  useEffect(() => {
    if (!searchParams.get('sellerUID')) resetPhotoURL();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <LogoRoot
      ref={ref}
      underline="none"
      aria-label="Logo"
      component={RouterLink}
      className={mergeClasses([logoClasses.root, className])}
      href={`${href}${newSearchParams({ country: currentLang.countryName })}`}
      sx={[
        (theme) => ({
          height: 32,
          width: 'fit-content',
          color: theme.vars.palette.text.primary,
          ...(disabled && { pointerEvents: 'none' }),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {dinamic && photoURL ? (
        <Avatar src={photoURL} sx={{ width: 45, height: 45 }} />
      ) : (
        renderLogo(isSingle)
      )}
    </LogoRoot>
  );
}

const LogoRoot = styled(Link)(() => ({
  flexShrink: 0,
  color: 'transparent',
  display: 'inline-flex',
  verticalAlign: 'middle',
}));

const renderLogo = (isSingle) =>
  isSingle ? (
    <SvgIcon viewBox="0 0 500 500" sx={{ width: 1, height: 1 }}>
      <path
        fill="currentColor"
        d="m70.7 238.2-.1.4q5.2 11 13.2 19.5 14.2 15 33 21.3 15.7 5.1 33.3 3.8 28.7-2 49.3-21.6h.2l13.3 16q.2 0 0 .2-13.2 12-29.7 19.1-12.6 5.5-26.3 7.1-38.2 4.5-70-16A101 101 0 0 1 49 161q14.7-31.7 46.5-48.5 21.3-11.3 46-11.8 16.8-.4 32.4 5.4a103 103 0 0 1 60.8 67.6v.3zm-7-22.4.3 1.7q0 .6.5.3L206.6 162a.3.3 0 0 0 .2-.4q-7.6-15-21.1-25.5-20.9-16.2-47.2-14.5-3.5.2-7 .7a81 81 0 0 0-63.8 51q-7.8 20.7-4 42.5"
      />
      <path
        fill="#ff061c"
        d="m239.6 84.2.3 50a.2.2 0 0 1-.4.2q-.5-.7-1.2-1L222.7 122q-16.9-12.5-26.7-19.5-3.5-2.5-4.9-6.2-.5-1.4-.5-3.8-.2-5.7-.2-14l-.3-40q.1-.6.5-.4l11.1 8.3L234.1 70q4 3 5 7.2.5 1.5.5 7"
      />
      <path
        fill="none"
        stroke="#fc4d58"
        strokeWidth="18"
        strokeLinecap="round"
        d="M402 408.8c-20.1 0 9.4 0 0 0m0 0-97.2-.3c-6 0-8-10-9.9-15 0 0-48.4-125.7-50-127.5a13 13 0 0 0-5.4-3.5c-2.5-.9-5.5-.9-11.5-.9h-9.3m42.3 31.6h165.5c9.5 0 14.2 0 17.4 1.6q4.3 2.1 5.7 6c1 3-.3 6.6-3 13.9l-18 50.4c-1.6 4.4-2.4 6.6-4 8.2a13 13 0 0 1-5.3 3.2c-2.4.8-5.2.8-10.8.8H295.2"
      />
      <path
        stroke="#fc4d58"
        strokeWidth="18"
        fill="transparent"
        d="M321.2 428.8a19.2 19.2 0 1 1-38.5 0 19.2 19.2 0 0 1 38.5 0ZM428 428a19.2 19.2 0 1 1-38.5 0 19.2 19.2 0 0 1 38.5 0Z"
      />
      <path
        fill="#d8d8d8"
        stroke="#fc4d58"
        strokeWidth="10"
        d="m275.6 337.8 158.7.3m-108.5-44.3 5.7 81.9m53.4-82.6-7.4 81.2"
      />
    </SvgIcon>
  ) : (
    <SvgIcon viewBox="220 0 500 250" sx={{ width: 1, height: 1 }}>
      <path
        fill="currentColor"
        d="M337.8 77.3c-13.6-3.9-19.7-15.8-16.5-29.3a29 29 0 0 1 22.2-21c20.4-4.7 47.2 2.3 47.7 27.7q-.1 1.6-1.7 1.8h-8.7q-1.4-.1-1.7-1.6-1.5-9.7-9-14.2c-10.5-6.4-36.2-5.8-37.3 11.4-.6 9.4 7.4 12.8 15.2 14.7l24.5 5.8c13.3 3 22.8 12.9 21.7 27.2a32 32 0 0 1-4.2 13.9c-10 14-32 16.2-47.7 12.6a32 32 0 0 1-21.6-16.4 54 54 0 0 1-3.9-14.4q.1-1.3 1.4-1.5l9.7-.3q1.3 0 1.7 1.3c1.3 4.1 2 9.3 4.5 12.5q4.9 6.3 13 8c9.4 2.2 25.6 1.6 31.8-7.5q3-4.4 2.5-9.8c-1-11.5-13.7-13.7-22.9-15.7q-10.4-2.3-20.7-5.2m83.9-13.1q-.4 2.6-.4 6.6v35.3c0 8.2 1 10 9.4 10.2.4 0 1.7.2 2.2.7 1.4 1.2 1.6 8.3-1.2 8.9-5.2 1.1-14 .3-18.5-4-3.6-3.3-3.8-10-3.7-15.3l-.1-32c0-2.5.1-9.2-.1-11.3-.3-1.8-6.3-.5-8.5-1.2q-.5 0-.7-1.2v-4.2q0-1 .6-1c4.1-.7 9 .8 9-1.7.1-4.7.2-13.3.5-17.5q0-1.5 1.2-1.8a25 25 0 0 1 8.2 0q1.7.4 1.7 1.9c0 5.4-.3 15.8 0 17.2.4 1 1.6.9 4.1 1 5.5 0 7.3-.5 7.5 3.7.2 4.4-1.6 5-3.2 4.9l-7.2.1q-.6 0-.8.7M444 78.4a32 32 0 0 1 21.4-24.2q6.3-1.8 8.4-1.8C494 52.2 506.3 64.7 508 84c1 11.8-2 27-11.2 35.5a31 31 0 0 1-20.4 7.8 29 29 0 0 1-30-17 51 51 0 0 1-2.3-32m13.6-2.8c-5.2 12-4.4 36 11 40.7a22 22 0 0 0 15-.7c13.2-5.2 13.8-28.8 10.6-39.3A19 19 0 0 0 478 63q-14.1-1.8-20.3 12.5m78.9-10.9c1.7-2.6 3-5.2 5.3-7a22 22 0 0 1 15.3-5q.7 0 .7.7L558 64q-.1 1.1-1.1 1.2c-8.7.1-16.5 2.7-19.9 11.1q-1.5 3.6-1.6 12.7v35q0 1.2-1.2 1.3l-9.4-.1q-1.3 0-1.3-1.3V55.3q0-.9.9-1 4-.3 8.4-.1 2.3 0 2 2.3-.3 4.2 1 8 .4.7.8.1m39.3 30.8c.7 8.7 4.6 18.6 14 21q11.2 2.8 19.2-4c2.7-2.2 4.6-6.4 7-9.5l.3-.2 8.8.3c.7 0 1.2.7 1 1.3-5.9 28.1-44 30.7-57.3 8.5a39 39 0 0 1-5.2-16.7q-.9-9 1.3-17.8 3.8-15 17.4-22.2a39 39 0 0 1 17.4-3.2c12.9 1.9 21 7.4 25.4 20a50 50 0 0 1 2.7 20q0 .6-.7.7h-48.1q-1.6 0-2.9.9a1 1 0 0 0-.3.9m2-11.2 36-.1q1.2-.1 1.3-1.3v-.2c0-10.7-8.7-19.4-19.3-19.3h-.2a19.3 19.3 0 0 0-19.1 19.5v.1q0 1.2 1.2 1.3M271.6 62q6.4 0 11.8 3.7 5.3 3.6 8.5 9.3l-23.7 9-23.6 9.2q-.9-5.7.6-11.2 1.4-5.5 5-9.9 3.5-4.5 9.1-7.3 5.7-2.7 12.3-2.8m24.7 54.8-6-7.3a26 26 0 0 1-18.7 7.6q-8.1 0-14.5-4a26 26 0 0 1-9.6-10.6l57-22a45 45 0 0 0-12-20q-4.4-3.7-9.7-6a29 29 0 0 0-11.2-2.1 37 37 0 0 0-26.4 10.8 38 38 0 0 0-11 26.4 36 36 0 0 0 11 26.4 37 37 0 0 0 40 8.2q6.5-2.7 11.1-7.4m346.9-67.2h7.5q3 0 4.7 1.5a5 5 0 0 1 1.6 4q0 1.6-1 3-.7 1.5-2.7 2l4.4 7.8h-4.5l-3.8-7.2h-2v7.2h-4.2zm4.2 8.1.6.1h1.1q1.6 0 2.5-.6 1-.7 1-2.1 0-1.2-.7-1.8-.8-.7-2-.7h-2.5zM632.6 59a17 17 0 0 1 5-12 18 18 0 0 1 7.6-4.4A17.3 17.3 0 0 1 667 59a17 17 0 0 1-5 12 18 18 0 0 1-16.8 4.3 17 17 0 0 1-10.3-7.8 17 17 0 0 1-2.3-8.5m3.4 0a14 14 0 0 0 8.4 13 13 13 0 0 0 9 .5 14 14 0 0 0 8.3-6.4 14 14 0 0 0 .8-12.6A14 14 0 0 0 655 46a13 13 0 0 0-10.7 0 14 14 0 0 0-8.4 13M282.4 204.8v-43h8.6l10.2 30.4 2 6.4 2.3-7 10.3-29.8h7.6v43H318v-36l-12.5 36h-5.2L288 168.2v36.6zm69.8-3.9q-3 2.6-5.6 3.5-2.8 1-5.8 1-5 0-8-2.4a8 8 0 0 1-2.7-6.4 9 9 0 0 1 3.8-7.2q1.7-1.2 3.8-1.8 1.5-.4 4.7-.8a48 48 0 0 0 9.4-1.8v-1.4q0-3.2-1.5-4.5-2-1.8-6-1.8-3.7 0-5.5 1.3t-2.6 4.6l-5.2-.7q.8-3.3 2.4-5.3t4.6-3.2q3-1 7-1t6.6.9 3.6 2.3q1.1 1.5 1.7 3.6l.2 4.9v7q0 7.4.4 9.3.3 2 1.3 3.8h-5.5q-1-1.7-1-3.9m-.4-11.8q-3 1.2-8.6 2a20 20 0 0 0-4.6 1q-1.4.7-2.1 1.8t-.7 2.5q0 2.1 1.6 3.5t4.6 1.4 5.4-1.3a8 8 0 0 0 3.5-3.6q.9-1.8.9-5.3zm13.4 15.7v-31.2h4.8v4.8q1.8-3.3 3.3-4.4 1.5-1 3.4-1 2.7 0 5.4 1.6l-1.8 5a8 8 0 0 0-3.9-1.2 5 5 0 0 0-3 1q-1.5 1-2 3-.9 2.7-.9 6v16.4zm20.1 0v-43h5.3v24.5l12.5-12.7h6.8l-12 11.6 13.2 19.6h-6.5l-10.3-16-3.7 3.6v12.4zm51.3-10 5.4.6q-1.2 4.8-4.8 7.4a14 14 0 0 1-8.9 2.7q-6.7 0-10.8-4.2-4-4.2-4-11.8 0-7.9 4-12.2 4.2-4.4 10.5-4.4t10.2 4.3 4 12v1.4H419a12 12 0 0 0 2.9 7.8q2.6 2.7 6.5 2.7a8 8 0 0 0 5-1.5q2-1.5 3.2-4.9m-17.3-8.6h17.3q-.3-4-2-5.9a8 8 0 0 0-6.5-3q-3.6 0-6.1 2.4a10 10 0 0 0-2.7 6.5M460 200l.8 4.7q-2.2.5-4 .5a9 9 0 0 1-4.4-1 5 5 0 0 1-2.3-2.3q-.6-1.4-.6-6.3v-17.9h-3.9v-4h3.9v-7.8l5.2-3.1v10.8h5.3v4.1h-5.3V196q0 2.3.3 3 .3.6 1 1 .6.3 1.7.3l2.3-.2m5.2 16.7v-43h4.8v4q1.6-2.4 3.9-3.6 2.1-1.2 5.1-1.2 4 0 7 2 3.1 2.2 4.7 5.9 1.5 3.7 1.5 8.2 0 4.8-1.7 8.6a13 13 0 0 1-11.8 7.9 10 10 0 0 1-4.8-1.2q-2-1-3.4-2.8v15.2zm4.8-27.3q0 6 2.4 8.9 2.4 2.8 6 2.8 3.4 0 6-3 2.4-3 2.4-9.2 0-6-2.4-8.9t-5.9-3-6 3.2-2.5 9.2m28.5 15.4v-43h5.2v43zm33.7-3.9q-3 2.6-5.6 3.5-2.7 1-5.8 1-5.1 0-8-2.4a8 8 0 0 1-2.7-6.4 9 9 0 0 1 3.8-7.2q1.7-1.2 3.8-1.8 1.5-.4 4.7-.8a48 48 0 0 0 9.4-1.8v-1.4q0-3.2-1.5-4.5-2-1.8-6-1.8-3.7 0-5.5 1.3t-2.6 4.6l-5.2-.7q.8-3.3 2.3-5.3 1.7-2 4.7-3.2 3-1 7-1t6.5.9q2.6 1 3.7 2.3 1.2 1.5 1.6 3.6.3 1.4.3 4.9v7q0 7.4.3 9.3.4 2 1.4 3.8h-5.5q-1-1.7-1-3.9m-.4-11.8q-3 1.2-8.6 2a20 20 0 0 0-4.6 1q-1.4.7-2.1 1.8t-.8 2.5q0 2.1 1.6 3.5t4.7 1.4 5.4-1.3a8 8 0 0 0 3.5-3.6q.9-1.8.9-5.3zm33.8 4.3 5.2.6q-.8 5.4-4.4 8.4-3.5 3-8.6 3-6.4 0-10.2-4.1-4-4.2-4-12 0-5 1.7-8.8 1.8-3.7 5.1-5.7a15 15 0 0 1 7.5-1.9q5 0 8.2 2.6 3.3 2.5 4.2 7.3l-5.2.8q-.6-3.2-2.5-4.7a7 7 0 0 0-4.5-1.6q-4 0-6.5 2.8-2.5 3-2.5 9 0 6.4 2.4 9.2a8 8 0 0 0 6.3 2.8q3 0 5.2-1.9a9 9 0 0 0 2.6-5.8m31 1.3 5.4.7q-1.2 4.8-4.7 7.4a14 14 0 0 1-9 2.7q-6.7 0-10.8-4.2-4-4.2-4-11.8 0-7.9 4-12.2 4.2-4.4 10.6-4.4 6.1 0 10.2 4.3t4 12l-.1 1.4H579a12 12 0 0 0 2.9 7.8q2.6 2.7 6.5 2.7a8 8 0 0 0 5-1.5q2-1.5 3.2-4.9m-17.3-8.5h17.3q-.3-4-2-5.9a8 8 0 0 0-6.5-3q-3.6 0-6 2.4a10 10 0 0 0-2.8 6.5"
      />
      <path
        fill="#ff061c"
        d="m310.2 39 .1 21.3q0 .2-.1.1l-.6-.4-17.8-13.2q-1.5-1-2-2.6-.3-.6-.3-1.7V19.3l4.7 3.6 13.7 10q1.7 1.3 2.1 3.1.2.6.2 3"
      />
      <path
        fill="none"
        strokeWidth="7"
        stroke="#fc4d58"
        strokeLinecap="round"
        d="M681.2 204.8c-8.8 0 4 0 0 0m0 0-42.1-.1c-2.6 0-3.5-4.4-4.3-6.5 0 0-20.9-54.4-21.6-55.1a6 6 0 0 0-2.3-1.6c-1.1-.3-2.4-.3-5-.3h-4m18.3 13.6h71.6c4 0 6 0 7.5.7q1.8 1 2.4 2.6c.5 1.3-.1 2.8-1.2 6l-7.9 21.8c-.7 1.9-1 2.8-1.7 3.5q-1 1-2.3 1.4c-1 .4-2.2.4-4.7.4h-49"
      />
      <path
        strokeWidth="7"
        stroke="#fc4d58"
        fill="transparent"
        d="M646.2 213.5a8.3 8.3 0 1 1-16.6 0 8.3 8.3 0 0 1 16.6 0Zm46.2-.5a8.3 8.3 0 1 1-16.6 0 8.3 8.3 0 0 1 16.6 0Z"
      />
      <path
        fill="#d8d8d8"
        strokeWidth="5"
        stroke="#fc4d58"
        d="m626.5 174 68.6.2M648.2 155l2.5 35.5m21.8-35.7-3.3 35"
      />
    </SvgIcon>
  );
